import type { CsvColumn } from '@/lib/csv'
import type { MatchPairRow } from '@/types/database'

/**
 * The matches export, shaped for a mail merge.
 *
 * `get_match_pairs` returns one row per pair, which is the right shape for
 * reading results on screen and the wrong one for sending email: a merge needs
 * one row per *recipient*, with their matches spread across the columns. So a
 * single pair becomes two rows here — each person gets a row naming the other.
 *
 * Columns:
 *   First name | Recipient | Match 1 Name | Match 1 Contact | … Match N …
 *
 * `Recipient` is the email address the merge sends to; `Contact` is the match's
 * phone number, which is what the recipient is being given.
 */

export interface MailMergeMatch {
  name: string
  /** The match's phone number. Empty when we do not hold one. */
  contact: string
}

export interface MailMergeRow {
  firstName: string
  /** Email address of the person receiving this row. */
  recipient: string
  matches: MailMergeMatch[]
  /** Kept for stable ordering and for naming anyone with a gap. */
  matchmakingId: string
}

export interface MailMergeExport {
  rows: MailMergeRow[]
  /** Widest match count, never below the five the format specifies. */
  matchColumns: number
  /**
   * Matched people whose match has no phone number on file. Their row would go
   * out with an empty Contact, which makes the email pointless, so the UI warns
   * before the organiser sends it.
   */
  missingContact: { recipient: string; matchName: string }[]
}

/** The five pairs the agreed format specifies. */
const MINIMUM_MATCH_COLUMNS = 5

function fullName(first: string, last: string | null): string {
  return `${first} ${last ?? ''}`.trim()
}

/**
 * Turns pair rows into one row per matched person.
 *
 * Anyone who matched appears exactly once, however many matches they have.
 * People who submitted but matched with nobody are not here at all — they get a
 * different email, and a row of empty Match columns would be a strange way to
 * say so.
 */
export function buildMatchMailMerge(pairs: readonly MatchPairRow[]): MailMergeExport {
  const byPerson = new Map<string, MailMergeRow>()
  const missingContact: { recipient: string; matchName: string }[] = []

  function record(
    self: { id: string; first: string; last: string | null; email: string | null; number: string },
    other: { first: string; last: string | null; phone: string | null; number: string }
  ) {
    let row = byPerson.get(self.id)
    if (!row) {
      row = {
        firstName: self.first,
        recipient: self.email ?? '',
        matches: [],
        matchmakingId: self.number,
      }
      byPerson.set(self.id, row)
    }

    const name = fullName(other.first, other.last)
    const contact = other.phone ?? ''

    if (contact === '') {
      missingContact.push({ recipient: `${self.first} (${self.number})`, matchName: name })
    }

    row.matches.push({ name, contact })
  }

  for (const pair of pairs) {
    record(
      {
        id: pair.a_attendee_id,
        first: pair.a_first_name,
        last: pair.a_last_name,
        email: pair.a_email,
        number: pair.a_matchmaking_id,
      },
      {
        first: pair.b_first_name,
        last: pair.b_last_name,
        phone: pair.b_phone,
        number: pair.b_matchmaking_id,
      }
    )

    record(
      {
        id: pair.b_attendee_id,
        first: pair.b_first_name,
        last: pair.b_last_name,
        email: pair.b_email,
        number: pair.b_matchmaking_id,
      },
      {
        first: pair.a_first_name,
        last: pair.a_last_name,
        phone: pair.a_phone,
        number: pair.a_matchmaking_id,
      }
    )
  }

  const rows = [...byPerson.values()].sort((a, b) => a.matchmakingId.localeCompare(b.matchmakingId))

  for (const row of rows) {
    row.matches.sort((a, b) => a.name.localeCompare(b.name))
  }

  // Five columns unless someone genuinely has more. A form configured to allow
  // more than five picks can produce more than five matches, and dropping one
  // would mean a person never hears about someone who picked them back.
  const widest = rows.reduce((max, row) => Math.max(max, row.matches.length), 0)

  return {
    rows,
    matchColumns: Math.max(MINIMUM_MATCH_COLUMNS, widest),
    missingContact,
  }
}

/** Column definitions for the agreed header row. */
export function mailMergeColumns(matchColumns: number): CsvColumn<MailMergeRow>[] {
  const columns: CsvColumn<MailMergeRow>[] = [
    { header: 'First name', value: (row) => row.firstName },
    { header: 'Recipient', value: (row) => row.recipient },
  ]

  for (let index = 0; index < matchColumns; index++) {
    const position = index + 1
    columns.push({
      header: `Match ${String(position)} Name`,
      value: (row) => row.matches[index]?.name ?? '',
    })
    columns.push({
      header: `Match ${String(position)} Contact`,
      value: (row) => row.matches[index]?.contact ?? '',
    })
  }

  return columns
}
